import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from '../components/Navbar';
import ContactSection from '../components/ContactSection';
import PrismaticBurst from '../components/PrismaticBurst';
import SplitText from '../components/SplitText';
import CLIENT_LOGOS from '../components/clientLogos';

const SERVICES = [
  { slug: 'web-design', title: 'Web Design', desc: 'Beautiful, responsive websites crafted with precision and purpose.' ,
    icon: <svg width="28" height="28" viewBox="0 0 36 36" fill="none"><rect x="2" y="2" width="32" height="22" rx="4" stroke="currentColor" strokeWidth="1.5"/><path d="M12 34h12M18 24v10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> },
  { slug: 'branding', title: 'Branding', desc: 'Identity systems that communicate your values with clarity and style.',
    icon: <svg width="28" height="28" viewBox="0 0 36 36" fill="none"><circle cx="18" cy="18" r="14" stroke="currentColor" strokeWidth="1.5"/><path d="M12 18l4 4 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
  { slug: 'digital-marketing', title: 'Digital Marketing', desc: 'Strategy-led campaigns that drive real growth and engagement.',
    icon: <svg width="28" height="28" viewBox="0 0 36 36" fill="none"><path d="M6 28l8-10 6 6 4-6 6 10H6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><circle cx="28" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/></svg> },
  { slug: 'advertising', title: 'Advertising', desc: 'Campaigns that capture attention and convert audiences into customers.',
    icon: <svg width="28" height="28" viewBox="0 0 36 36" fill="none"><rect x="4" y="8" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M24 12l8-4v16l-8-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg> },
];


function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
}

function Counter({ to, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let s = 0;
    const step = Math.ceil(to / 55);
    const t = setInterval(() => { s = Math.min(s + step, to); setVal(s); if (s >= to) clearInterval(t); }, 18);
    return () => clearInterval(t);
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function ServiceCard({ svc, index, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(`/services/${svc.slug}`)}
      style={{
        background: hovered ? '#f4f4f4' : 'rgba(255,255,255,0.02)',
        border: `1px solid ${hovered ? '#f4f4f4' : 'rgba(255,255,255,0.07)'}`,
        borderRadius: '20px', padding: '48px 36px',
        cursor: 'pointer', minHeight: '300px',
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-6px) scale(1.01)' : 'none',
        boxShadow: hovered ? '0 24px 60px rgba(0,0,0,0.5)' : 'none',
        display: 'flex', flexDirection: 'column', gap: '14px',
      }}
    >
      <div style={{ color: hovered ? '#04080f' : 'rgba(240,240,248,0.5)', transition: 'color 0.3s' }}>
        {svc.icon}
      </div>
      <h4 style={{ fontFamily: "'Mona Sans', sans-serif", fontWeight: 700, fontSize: '20px',
        color: hovered ? '#04080f' : '#f0f0f8', transition: 'color 0.3s', marginTop: '4px' }}>
        {svc.title}
      </h4>
      <p style={{ fontSize: '13.5px', color: hovered ? 'rgba(4,8,15,0.65)' : 'rgba(240,240,248,0.38)',
        lineHeight: 1.75, transition: 'color 0.3s', flex: 1 }}>
        {svc.desc}
      </p>
      <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', letterSpacing: '0.15em',
        color: hovered ? '#325998' : 'rgba(240,240,248,0.18)', transition: 'color 0.3s' }}>
        EXPLORE →
      </span>
    </motion.div>
  );
}

export default function Home() {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#04080f', minHeight: '100vh' }}>
      <Navbar />

      {/* ══ HERO ══ */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', overflow: 'hidden', padding: '120px 24px 80px', className: undefined }}>
        <PrismaticBurst colors={['#325998','#101D32','#f4f4f4','#7aaad8']} />

        <div style={{ position: 'relative', zIndex: 5, maxWidth: '820px' }}>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.22,1,0.36,1] }}>
            <h1 style={{ fontFamily: "'Mona Sans',sans-serif", fontWeight: 800, fontSize: 'clamp(42px,7vw,82px)', lineHeight: 1.0, letterSpacing: '-0.025em', color: '#f0f0f8', marginBottom: '20px', whiteSpace: 'nowrap' }}>
              Always DOIT with us!
            </h1>
          </motion.div>

          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.28, ease: [0.22,1,0.36,1] }}
            style={{ fontSize: '15px', color: 'rgba(240,240,248,0.45)', marginBottom: '44px', letterSpacing: '0.01em' }}>
            One stop creative solution for your business.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.42, ease: [0.22,1,0.36,1] }}
            style={{ display: 'flex', gap: '14px', justifyContent: 'center' }}>
            <GlassBtn onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>Recent Work</GlassBtn>
            <GlassBtn onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} accent>Contact Us</GlassBtn>
          </motion.div>
        </div>
      </section>

      {/* ══ LOGO LOOP ══ */}
      <section style={{ padding: '48px 0', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden', background: 'rgba(255,255,255,0.01)', position: 'relative' }}>
        <div style={{ position:'absolute',left:0,top:0,bottom:0,width:'80px',background:'linear-gradient(90deg,#04080f,transparent)',zIndex:2,pointerEvents:'none'}}/>
        <div style={{ position:'absolute',right:0,top:0,bottom:0,width:'80px',background:'linear-gradient(-90deg,#04080f,transparent)',zIndex:2,pointerEvents:'none'}}/>
        <motion.div
          animate={{ x: [0, -(CLIENT_LOGOS.length * 212)] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
          style={{ display: 'flex', width: 'max-content' }}
        >
          {[...CLIENT_LOGOS, ...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '48px', flexShrink: 0, marginRight: '72px',
              opacity: 0.35,
              filter: 'brightness(10)',
            }}>
              <img src={logo.src} alt={logo.name} style={{ maxHeight: '40px', maxWidth: '140px', objectFit: 'contain' }} />
            </div>
          ))}
        </motion.div>
      </section>

      {/* ══ ABOUT ══ */}
      <section id="about" style={{ padding: '120px 48px 80px', textAlign: 'center' }}>
        <Reveal>
          <p style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '0.2em', color: '#325998', marginBottom: '14px', textTransform: 'uppercase' }}>About Us</p>
          <h2 style={{ fontFamily: "'Mona Sans',sans-serif", fontWeight: 800, fontSize: 'clamp(30px,5vw,58px)', letterSpacing: '-0.02em', color: '#f0f0f8', marginBottom: '14px' }}>
            <SplitText text="We DO Whatever IT Takes." />
          </h2>
          <p style={{ fontSize: '14px', color: 'rgba(240,240,248,0.4)', maxWidth: '380px', margin: '0 auto 52px', lineHeight: 1.8 }}>
            We're DOIT CREATIVE — your one and only creative solution for your business.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ maxWidth: '700px', margin: '0 auto 52px', borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', aspectRatio: '16/9', background: '#0a1220', boxShadow: '0 40px 80px rgba(0,0,0,0.6)' }}>
            <iframe width="100%" height="100%"
              src="https://www.youtube.com/embed/1OZnXQQPBU0?autoplay=0&mute=1&controls=1&rel=0"
              title="Cinematic reel" style={{ border: 'none', display: 'block' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '2px', maxWidth: '700px', margin: '0 auto' }}>
            {[{ n:199,s:'+',l:'Projects' },{ n:98,s:'%',l:'Satisfaction' },{ n:6,s:'+',l:'Years' },{ n:20,s:'+',l:'Clients' }].map(({ n,s,l }, i) => (
              <motion.div key={l} initial={{ opacity:0,y:20 }} whileInView={{ opacity:1,y:0 }} viewport={{ once:true }} transition={{ delay: i*0.08, duration:0.55 }}
                style={{ padding:'32px 16px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius: i===0?'14px 0 0 14px': i===3?'0 14px 14px 0':'0' }}>
                <div style={{ fontFamily:"'Mona Sans',sans-serif", fontWeight:800, fontSize:'34px', color:'#f0f0f8' }}><Counter to={n} suffix={s} /></div>
                <div style={{ fontSize:'10px', color:'rgba(240,240,248,0.3)', letterSpacing:'0.1em', textTransform:'uppercase', marginTop:'4px' }}>{l}</div>
              </motion.div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ══ FROM SCRATCH ══ */}
      <section id="services" style={{ padding: '80px 48px', maxWidth: '1100px', margin: '0 auto' }} className='section-pad'>
        <Reveal>
          <h2 style={{ fontFamily:"'Mona Sans',sans-serif", fontWeight:800, fontSize:'clamp(50px,9vw,100px)', lineHeight:1.0, letterSpacing:'-0.03em', color:'#f0f0f8' }}>
            <SplitText text="From scratch" stagger={0.08} />
            <br/>
            <SplitText text="to catch." stagger={0.08} delay={0.18} wordStyle={{ color:'rgba(240,240,248,0.22)' }} />
          </h2>
        </Reveal>
      </section>

      {/* ══ SERVICES GRID ══ */}
      <section style={{ background:'rgba(255,255,255,0.012)', padding:'100px 48px 140px', borderTop:'1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth:'1100px', margin:'0 auto' }}>
          <Reveal>
            <p style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'11px', letterSpacing:'0.2em', color:'#325998', marginBottom:'16px', textTransform:'uppercase' }}>What We Offer</p>
            <h2 style={{ fontFamily:"'Mona Sans',sans-serif", fontWeight:800, fontSize:'clamp(30px,4vw,52px)', letterSpacing:'-0.022em', color:'#f0f0f8', marginBottom:'56px', lineHeight:1.05 }}>
              <SplitText text="Everything your brand needs," stagger={0.055} />
              <br/>
              <SplitText text="under one roof." stagger={0.055} delay={0.25} wordStyle={{ color:'rgba(240,240,248,0.28)' }} />
            </h2>
          </Reveal>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:'16px' }}>
            {SERVICES.map((svc, i) => <ServiceCard key={svc.slug} svc={svc} index={i} navigate={navigate} />)}
          </div>
        </div>
      </section>

      <ContactSection />

      <footer style={{ borderTop:'1px solid rgba(255,255,255,0.05)', padding:'32px 48px', display:'flex', alignItems:'center', justifyContent:'space-between', maxWidth:'1200px', margin:'0 auto', flexWrap:'wrap', gap:'16px' }}>
        <p style={{ fontSize:'11px', color:'rgba(240,240,248,0.2)', letterSpacing:'0.05em' }}>© 2026 DOIT Creative. All rights reserved.</p>
        <p style={{ fontFamily:"'Share Tech Mono',monospace", fontSize:'10px', color:'rgba(240,240,248,0.12)', letterSpacing:'0.15em' }}>DOIT_CREATIVE / EST.2020</p>
      </footer>
    </div>
  );
}

function GlassBtn({ children, onClick, accent }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
      style={{ padding:'13px 30px', background: hov ? (accent?'rgba(50,89,152,0.55)':'rgba(255,255,255,0.16)') : (accent?'rgba(50,89,152,0.25)':'rgba(255,255,255,0.07)'),
        border:`1px solid ${accent?'rgba(50,89,152,0.6)':'rgba(255,255,255,0.22)'}`,
        borderRadius:'999px', backdropFilter:'blur(18px)', WebkitBackdropFilter:'blur(18px)',
        color:'#f0f0f8', fontFamily:"'Mona Sans',sans-serif", fontSize:'13.5px', fontWeight:500,
        cursor:'pointer', letterSpacing:'0.03em', transition:'background 0.25s, border-color 0.25s',
        boxShadow: accent?'0 0 28px rgba(50,89,152,0.3)':'none' }}>
      {children}
    </motion.button>
  );
}

// Mobile responsive inline styles injected via style tag
// (handled by MobileStyles component in App.js)
